// This creates a simple local cache that stores values by a key. The cache can be configured with maxEntries which limits it to a certain number of entries, and when the cache is full, the oldest entry is removed.
export class SimpleLocalCache<DataType, Keys extends string | { [key: string]: string }> {
  private _internalData: { key: string; data: DataType; timestamp: number }[] = [];
  private _internalPointer: number = 0;
  private _internalIndex: { [key: string]: { key: string; data: DataType } | undefined } = {};
  private readonly keyBuilder: (args: Keys) => string = (args: Keys) =>
    typeof args === 'string'
      ? args
      : Object.keys(args)
          .sort()
          .map((k) => args[k])
          .join(':');
  private readonly maxEntries: number = Infinity;
  constructor({ keyBuilder, maxEntries }: { keyBuilder?: (args: Keys) => string; maxEntries?: number } = {}) {
    if (keyBuilder) {
      this.keyBuilder = keyBuilder;
    }
    if (maxEntries) {
      this.maxEntries = maxEntries;
    }
  }
  add = (params: Keys, data: DataType) => {
    const key = this.keyBuilder(params);
    const timestamp = new Date().valueOf();
    if (this._internalPointer >= this.maxEntries) {
      this._internalPointer = 0;
    }
    const exists = this._internalData.at(this._internalPointer);
    if (exists && this._internalIndex[exists.key] !== undefined) {
      this._internalIndex[exists.key] = undefined;
    }
    this._internalData[this._internalPointer] = { key, data, timestamp };
    this._internalIndex[key] = this._internalData[this._internalPointer];
    this._internalPointer += 1;
  };
  get = (params: Keys): DataType | undefined => {
    const key = this.keyBuilder(params);
    const record = this._internalIndex[key];
    if (record && record.key === key) {
      return record.data;
    }
    return undefined;
  };
  clear() {
    this._internalIndex = {};
    this._internalData.length = 0;
    this._internalPointer = 0;
  }
}
